import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Sparkles } from "lucide-react";
import { api } from "@/api/client";
import { calcPoints } from "@/lib/scoring";
import { appUrl, shareInvite } from "@/lib/share";
import { BONUS_MAX_POINTS } from "@/lib/bonus";

// Personal end-of-tournament summary — the "your season in numbers" card that
// replaces the tipping CTAs once the Tippspiel is decided.
//
// `games` / `tips` are passed in rather than refetched: the Dashboard already
// holds both, and this component renders directly underneath it.

export function SeasonWrapUp({
  games,
  tips,
  rank,
  totalTippers,
  totalPoints,
  bestStreak,
  username,
}: {
  games: any[];
  tips: Record<string, any>;
  rank: number | null;
  totalTippers: number;
  totalPoints: number;
  bestStreak?: number;
  username?: string;
}) {
  const [bonusPoints, setBonusPoints] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    api.bonus.me()
      .then((b: any) => { if (alive) setBonusPoints(b?.pointsEarned ?? 0); })
      .catch(() => { if (alive) setBonusPoints(0); });
    return () => { alive = false; };
  }, []);

  // Hit breakdown across every finished game the user actually tipped.
  const stats = useMemo(() => {
    let exact = 0, diff = 0, tendency = 0, miss = 0, tipped = 0;
    for (const g of games) {
      const t = tips[g.id];
      if (!t) continue;
      if (!g.is_finished || g.home_score == null || g.away_score == null) continue;
      tipped++;
      const p = calcPoints(g.home_score, g.away_score, t.home_tip, t.away_tip);
      if (p === 4) exact++;
      else if (p === 3) diff++;
      else if (p === 2) tendency++;
      else miss++;
    }
    const hitRate = tipped > 0 ? Math.round(((exact + diff + tendency) / tipped) * 100) : 0;
    return { exact, diff, tendency, miss, tipped, hitRate };
  }, [games, tips]);

  const share = () => {
    const place = rank ? `Platz ${rank}${totalTippers ? ` von ${totalTippers}` : ""}` : "dabei gewesen";
    shareInvite(
      `⚽ Meine WM 2026 im Englputzeder Tippspiel:\n\n` +
        `🏅 ${place}\n` +
        `🎯 ${totalPoints} Punkte\n` +
        `✅ ${stats.exact}× Volltreffer bei ${stats.tipped} Tipps (${stats.hitRate} % Trefferquote)\n\n` +
        `${appUrl()}`,
      "Meine WM-Bilanz",
    );
  };

  return (
    <section className="bg-card rounded-lg shadow-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-accent-foreground" />
          <h2 className="font-display font-extrabold text-base leading-none">
            Deine WM-Bilanz{username ? `, ${username}` : ""}
          </h2>
        </div>
        <button
          onClick={share}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-pill bg-accent text-accent-foreground px-3 py-1.5 text-[12px] font-bold tap"
        >
          <Share2 className="h-3.5 w-3.5" /> Teilen
        </button>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-3 gap-2">
        {/* Short label on purpose: at 375px these are ~104px tiles and
            "Endplatzierung" overflows its box. */}
        <Big label="Endplatz" value={rank ? `#${rank}` : "—"} sub={totalTippers ? `von ${totalTippers}` : undefined} accent />
        <Big label="Punkte" value={totalPoints} sub={bonusPoints != null ? `davon ${bonusPoints} Bonus` : undefined} />
        <Big label="Trefferquote" value={`${stats.hitRate} %`} sub={`${stats.tipped} Tipps`} />
      </div>

      {/* Hit breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Small emoji="🎯" label="Volltreffer" value={stats.exact} hint="4 Pkt" />
        <Small emoji="➖" label="Tordifferenz" value={stats.diff} hint="3 Pkt" />
        <Small emoji="↗️" label="Tendenz" value={stats.tendency} hint="2 Pkt" />
        <Small emoji="❌" label="Daneben" value={stats.miss} hint="0 Pkt" />
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
        {bonusPoints != null && (
          <span className="rounded-pill bg-muted px-2 py-1">
            🎁 Bonus-Fragen: <b className="text-foreground tabular-nums">{bonusPoints}</b> / {BONUS_MAX_POINTS} Pkt
          </span>
        )}
        {bestStreak != null && bestStreak > 0 && (
          <span className="rounded-pill bg-muted px-2 py-1">
            🔥 Längste Serie: <b className="text-foreground tabular-nums">{bestStreak}</b> Tage
          </span>
        )}
      </div>

      <Link
        to="/ranking"
        className="block rounded-md bg-muted/60 hover:bg-muted transition p-2.5 text-center text-[12px] font-bold tap"
      >
        Endstand aller Tipper ansehen →
      </Link>
    </section>
  );
}

function Big({ label, value, sub, accent }: { label: string; value: any; sub?: string; accent?: boolean }) {
  return (
    <div className={`rounded-lg p-3 text-center overflow-hidden ${accent ? "bg-accent text-accent-foreground" : "bg-muted/60"}`}>
      {/* No break-words: it splits "TREFFERQUOTE" mid-word at 375px. Labels
          are kept short enough to fit instead. */}
      <div className="text-[10px] font-bold uppercase tracking-wide opacity-70 leading-tight">{label}</div>
      <div className="font-display font-extrabold text-xl tabular-nums leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-70 leading-tight truncate">{sub}</div>}
    </div>
  );
}

function Small({ emoji, label, value, hint }: { emoji: string; label: string; value: number; hint: string }) {
  return (
    <div className="rounded-md border border-border p-2 flex items-center gap-2">
      <span className="text-base leading-none shrink-0">{emoji}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground leading-tight truncate">{label}</div>
        <div className="font-display font-extrabold text-sm tabular-nums leading-tight">
          {value}
          {/* "×" separator, not bare whitespace: "12" next to "4 Pkt" reads
              as the number 124 at a glance (and in copied text). */}
          <span className="text-[9px] font-semibold text-muted-foreground ml-1">× {hint}</span>
        </div>
      </div>
    </div>
  );
}
